import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/openplay/OpenPlayIcon';

const LocationSearchBar = ({
  onSearch,
  onUseCurrentLocation,
  onQueryChange,
  suggestions,
  onSelectSuggestion,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputWrapperRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  const mockSuggestions = [
    { id: '1', name: '강남역', address: '서울특별시 강남구 강남대로' },
    { id: '2', name: '홍대입구역', address: '서울특별시 마포구 양화로' },
    { id: '3', name: '잠실종합운동장', address: '서울특별시 송파구 올림픽로' },
  ];

  const effectiveSuggestions = useMemo(() => {
    if (Array.isArray(suggestions)) return suggestions;
    return mockSuggestions;
  }, [suggestions]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    onSearch(query);
    if (typeof onQueryChange === 'function') onQueryChange(query);
    setShowSuggestions(false);
  };

  const handleSelect = (suggestion) => {
    const label = String(suggestion?.name || suggestion?.address || '').trim();
    if (!label) return;
    handleSearch(label);
    if (typeof onSelectSuggestion === 'function') onSelectSuggestion(suggestion);
  };

  useEffect(() => {
    if (!showSuggestions) return;

    const updateRect = () => {
      const el = inputWrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownRect({
        left: rect.left,
        top: rect.bottom,
        width: rect.width,
      });
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [showSuggestions]);

  return (
    <div className="relative w-full">
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1" ref={inputWrapperRef}>
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const next = e.target.value;
              setSearchQuery(next);
              if (typeof onQueryChange === 'function') onQueryChange(next);
              setShowSuggestions(next.length > 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch(searchQuery);
              }
            }}
            onFocus={() => setShowSuggestions(searchQuery.length > 0)}
            placeholder="장소, 주소 검색"
            className="w-full h-12 pl-12 pr-4 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
          />
        </div>
        <button
          onClick={onUseCurrentLocation}
          className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-primary-foreground hover:shadow-elevation-2 transition-smooth touch-target active:scale-95"
          aria-label="현재 위치"
          type="button"
        >
          <Icon name="MapPinIcon" variant="solid" size={24} />
        </button>
      </div>

      {showSuggestions &&
        dropdownRect &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[10050]" onClick={() => setShowSuggestions(false)} />
            <div
              className="fixed mt-2 bg-popover rounded-lg shadow-elevation-3 border border-border z-[10060] overflow-hidden"
              style={{ left: dropdownRect.left, top: dropdownRect.top, width: dropdownRect.width }}
            >
              <div className="p-2">
                {effectiveSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSelect(suggestion)}
                    className="w-full flex items-start gap-3 px-4 py-3 rounded-lg text-left hover:bg-muted transition-smooth touch-target"
                    type="button"
                  >
                    <Icon name="MapPinIcon" size={20} className="text-muted-foreground mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{suggestion.name}</p>
                      <p className="text-xs text-muted-foreground caption truncate">{suggestion.address}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default LocationSearchBar;
